/**
 * The 'Case insensitive' Map
 *
 * Internally this converts the keys to lowercase. This is useful for lookups.
 * Note: passing `undefined` or `null` will normalize the key to `""` (empty string).
 *
 * @example
 * ```ts
  const monthMap = new CiMap<string>([
    ['Jan', '01'],
    ['Feb', '02'],
  ]);
  console.debug(monthMap.get('jan')) -> 01
  console.debug(monthMap.get('JAN')) -> 01
 * ```
 */
export class CiMap<T> extends Map<string, T> {
  // NOTE: The constructor will iterate and call our overridden set()

  set(key: string | undefined, value: T): this {
    super.set(key?.toLowerCase() ?? "", value)
    return this
  }

  get(key: string | undefined): T | undefined {
    return super.get(key?.toLowerCase() ?? "")
  }

  has(key: string | undefined): boolean {
    return super.has(key?.toLowerCase() ?? "")
  }

  delete(key: string | undefined): boolean {
    return super.delete(key?.toLowerCase() ?? "")
  }

  /**
   * Construct case insensitive map from a Record
   * @example
   * ```ts
   * CiMap.fromRecord({
      Jan: "01",
      January: "01",
      Feb: "02"
    })
   * ```
   */
  static fromRecord<T>(record: Record<string, T>): CiMap<T> {
    return new CiMap<T>(Object.entries(record))
  }

  /**
   * Convert back to a plain Record object, with all keys normalized to
   * lowercase.
   */
  toRecord(): Record<string, T> {
    return Object.fromEntries(this.entries())
  }
}
